import { viewerFeature } from './viewer.reducer'
import { createSelector, MemoizedSelector } from '@ngrx/store'
import { LayerConfig, StaticLayerConfig, ViewerState } from './viewer.state'
import { LayerType } from '../../shared/model/layers/layer-types'
import { getLayerIdsForGroupId, hasDisplayableLoadedLayers } from './viewer-state-utils'
import { isManagedLayer } from './viewer-layer-utils'

export const {
    selectViewerState,
    selectLayers,
    selectLayerGroups,
    selectSearchKey,
    selectLayerListDialogOpen,
} = viewerFeature

export const selectSelectableSegmentationLayerNames = createSelector(
    selectViewerState,
    (state: ViewerState): string[] =>
        (state.layers ?? [])
            .filter(l => l.type === LayerType.Segmentation && l.isSelectable)
            .map(l => l.name)
            .filter((name: string | undefined): name is string => name !== undefined)
)

export const selectSelectableAnnotationLayerNames = createSelector(
    selectViewerState,
    (state: ViewerState): string[] =>
        (state.layers ?? [])
            .filter(l => l.type === LayerType.Annotation && l.isSelectable)
            .map(l => l.name)
            .filter((name: string | undefined): name is string => name !== undefined)
)

export const selectSelectableAnnotationLayerUrls = createSelector(
    selectViewerState,
    (state: ViewerState): string[] =>
        (state.layers ?? [])
            .filter(l => l.type === LayerType.Annotation && l.isSelectable)
            .map(l => l.source)
            .filter((source: string | undefined): source is string => source !== undefined)
)

export const selectImageLayers = createSelector(
    selectViewerState,
    (state: ViewerState): StaticLayerConfig[] =>
        (state.layers ?? []).filter(l => l.type === LayerType.Image)
)

export const selectSegmentationLayers = createSelector(
    selectViewerState,
    (state: ViewerState): StaticLayerConfig[] =>
        (state.layers ?? []).filter(l => l.type === LayerType.Segmentation)
)

export const selectLoadedSegmentationSources = createSelector(
    selectSegmentationLayers,
    (layers): string[] =>
        layers.map(l => l.source).filter((source): source is string => !!source)
)

export const selectAnnotationLayers = createSelector(
    selectViewerState,
    (state: ViewerState): StaticLayerConfig[] =>
        (state.layers ?? []).filter(l => l.type === LayerType.Annotation && isManagedLayer(l))
)

export const selectLayerInfos = createSelector(
    selectViewerState,
    (
        state: ViewerState
    ): {
        name: string | undefined
        source: string | undefined
        id: string | undefined | null
    }[] => (state.layers ?? []).map(l => ({ name: l.name, source: l.source, id: l.id }))
)

export const selectExpandedNodes = createSelector(
    selectViewerState,
    (state: ViewerState): string[] => {
        return state.expandedNodes ?? []
    }
)

export const selectInvisibleLayers = createSelector(
    selectViewerState,
    (state: ViewerState): LayerConfig[] => state.invisibleLayers ?? []
)

export const selectHasDisplayableLoadedLayers = createSelector(
    selectViewerState,
    (state: ViewerState): boolean => hasDisplayableLoadedLayers(state)
)

// todo: ask ariadne to provide group ID and use that instead of name
export const selectLayerInfosForGroupId = (
    groupId: string
): MemoizedSelector<Record<string, any>, { source: string; name: string; id: string }[]> =>
    createSelector(selectViewerState, (state: ViewerState): { source: string; name: string; id: string }[] => {
        return getLayerIdsForGroupId(groupId, state)
            .map(id => state.layers?.find(layer => layer.id === id))
            .filter(
                (layer: LayerConfig): layer is LayerConfig & { name: string; source: string; id: string } =>
                    !!layer &&
                    !!layer?.name &&
                    !!layer?.source &&
                    !!layer?.id &&
                    isManagedLayer(layer)
            )
            .map(layer => ({ name: layer.name, source: layer.source, id: layer.id }))
    })
